import apiFetch from '../utils/apiFetch';
import {
  CUSTOM_ASSET_STORAGE_KEY,
  SESSION_INDEX_STORAGE_KEY,
  UNIFIED_ENTITY_STORAGE_KEY,
  buildUsageStatus,
  formatBytes,
  getTierConfig,
  isSessionExpired
} from '../config/entitlements';
import { persistResolvedEntitlements, readResolvedEntitlements, resolveRuntimeTier } from '../utils/runtimeTier';

const safeReadJson = (storageKey, fallbackValue) => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return fallbackValue;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : fallbackValue;
  } catch (_error) {
    return fallbackValue;
  }
};

const dedupeById = (items) => {
  const seen = new Map();
  items.forEach((item) => {
    if (!item) {
      return;
    }
    const id = String(item.id || item._id || item.key || Math.random()).trim();
    seen.set(id, item);
  });
  return Array.from(seen.values());
};

export const isCustomEntityRecord = (entity) => {
  if (!entity || typeof entity !== 'object') {
    return false;
  }

  const tags = [
    ...(Array.isArray(entity.tags) ? entity.tags : []),
    ...(Array.isArray(entity.metadata?.tags) ? entity.metadata.tags : []),
    ...(Array.isArray(entity._meta?.tags) ? entity._meta.tags : [])
  ].map((tag) => String(tag).toLowerCase());

  if (tags.includes('template')) {
    return false;
  }

  const sourceName = String(entity.source?.name || '').toLowerCase();
  if (sourceName.includes('template')) {
    return false;
  }

  if (tags.includes('custom') || tags.includes('upload')) {
    return true;
  }

  if (String(entity.id || '').startsWith('custom_')) {
    return true;
  }

  return Boolean(entity.userId || entity.ownerUserId || entity.createdAt || entity._meta?.createdAt || entity.metadata?.created);
};

const getLocalUnifiedEntities = (entityType) => {
  const data = safeReadJson(UNIFIED_ENTITY_STORAGE_KEY, {});
  const bucket = data?.[`${entityType}s`] || {};
  return Object.values(bucket).filter(isCustomEntityRecord);
};

const getLegacyCustomAssets = (entityType) => {
  const data = safeReadJson(CUSTOM_ASSET_STORAGE_KEY, { characters: [], monsters: [] });
  const bucket = data?.[`${entityType}s`] || [];
  return Array.isArray(bucket) ? bucket : [];
};

const normalizeResponseList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.assets)) {
    return payload.assets;
  }
  return [];
};

async function fetchOwnedEntities(entityType) {
  try {
    const response = await apiFetch(`/api/${entityType}s`);
    if (!response.ok) {
      return [];
    }
    const payload = await response.json();
    return normalizeResponseList(payload);
  } catch (_error) {
    return [];
  }
}

export async function getBackendEntitlements({ forceRefresh = false } = {}) {
  const cached = readResolvedEntitlements();
  if (!forceRefresh && cached?.userTier) {
    return cached;
  }

  try {
    const response = await apiFetch('/api/me/entitlements', { requireAuth: true });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const payload = await response.json();
    persistResolvedEntitlements(payload);
    return readResolvedEntitlements() || payload;
  } catch (_error) {
    return cached || null;
  }
}

export async function getCustomEntityUsageSnapshot(entityType) {
  const [remoteItems, localUnifiedItems] = await Promise.all([
    fetchOwnedEntities(entityType),
    Promise.resolve(getLocalUnifiedEntities(entityType))
  ]);
  const legacyItems = getLegacyCustomAssets(entityType);
  const items = dedupeById([
    ...remoteItems.filter(isCustomEntityRecord),
    ...localUnifiedItems,
    ...legacyItems
  ]);

  return {
    items,
    count: items.length,
    ids: new Set(items.map((item) => String(item.id || item._id || '').trim()).filter(Boolean))
  };
}

export async function assertCustomEntityCreationAllowed({ entityType, entityId, userTier = resolveRuntimeTier() }) {
  const config = getTierConfig(userTier);
  const limitKey = entityType === 'monster' ? 'customMonsters' : 'customCharacters';
  const status = buildUsageStatus(config.limits[limitKey], 0);

  if (status.isUnlimited) {
    return { allowed: true, status };
  }

  const usage = await getCustomEntityUsageSnapshot(entityType);
  if (entityId && usage.ids.has(String(entityId))) {
    return {
      allowed: true,
      isExisting: true,
      status: buildUsageStatus(config.limits[limitKey], usage.count)
    };
  }

  const nextStatus = buildUsageStatus(config.limits[limitKey], usage.count);
  if (nextStatus.atLimit) {
    const rosterLabel = entityType === 'monster' ? 'monster vault' : 'hero roster';
    const unitLabel = entityType === 'monster' ? 'custom monsters' : 'custom heroes and NPCs';
    throw new Error(`Your free ${rosterLabel} is full at ${nextStatus.limit} ${unitLabel}. Retire one or upgrade to Game Master.`);
  }

  return {
    allowed: true,
    status: nextStatus
  };
}

export async function getAssetUsageSnapshot() {
  try {
    const response = await apiFetch('/api/s3-uploads/list', { requireAuth: true });
    if (!response.ok) {
      return {
        assets: [],
        totalCount: 0,
        totalBytes: 0
      };
    }

    const payload = await response.json();
    const assets = normalizeResponseList(payload).filter((asset) => asset && asset.key);
    const totalCount = Number.isFinite(Number(payload?.totalCount)) ? Number(payload.totalCount) : assets.length;
    const totalBytes = Number.isFinite(Number(payload?.totalBytes))
      ? Number(payload.totalBytes)
      : assets.reduce((sum, asset) => sum + Number(asset.size || 0), 0);

    return {
      assets,
      totalCount,
      totalBytes
    };
  } catch (_error) {
    return {
      assets: [],
      totalCount: 0,
      totalBytes: 0
    };
  }
}

export async function assertAssetUploadAllowed({ fileSize = 0, userTier = resolveRuntimeTier() }) {
  const config = getTierConfig(userTier);
  const usage = await getAssetUsageSnapshot();
  const countStatus = buildUsageStatus(config.limits.assetCount, usage.totalCount);
  const storageStatus = buildUsageStatus(config.limits.assetStorageBytes, usage.totalBytes);

  if (!countStatus.isUnlimited && usage.totalCount + 1 > countStatus.limit) {
    throw new Error(`Your free asset vault is full at ${countStatus.limit} uploads. Clear a slot or upgrade to Game Master.`);
  }

  if (!storageStatus.isUnlimited && usage.totalBytes + Number(fileSize || 0) > config.limits.assetStorageBytes) {
    throw new Error(
      `Your asset vault is brushing the ${formatBytes(config.limits.assetStorageBytes)} storage cap. Clear space or upgrade your storage plan.`
    );
  }

  return {
    usage,
    countStatus,
    storageStatus
  };
}

export function getStoredSessionSnapshot() {
  const sessions = safeReadJson(SESSION_INDEX_STORAGE_KEY, []);
  const items = Array.isArray(sessions) ? sessions : [];
  return items.map((session) => ({
    ...session,
    isExpired: isSessionExpired(session)
  }));
}

export function getSessionUsageSnapshot(userTier = resolveRuntimeTier()) {
  const config = getTierConfig(userTier);
  const sessions = getStoredSessionSnapshot();
  return {
    sessions,
    status: buildUsageStatus(config.limits.savedSessions, sessions.length)
  };
}
